/**
 * RYFE — Whiteboard: Embed-Allowlist (Web-Embed / iframe auf dem Board).
 *
 * Excalidraw pflegt eine eigene Host-Allowlist (`ALLOWED_DOMAINS` in
 * packages/element/src/embeddable.ts: YouTube, Figma, Vimeo …). Die Fork-Pakete
 * bleiben unangetastet — stattdessen erweitern wir die Allowlist host-seitig über
 * den `validateEmbeddable`-Prop von <Excalidraw>.
 *
 * `validateRyfeEmbeddable` gibt `true` NUR für RYFE-eigene Embed-Hosts zurück und
 * sonst `undefined` — bei `undefined` fällt Excalidraw auf seine Standard-
 * Allowlist zurück, d. h. alle bisher erlaubten Fremd-Hosts bleiben erlaubt.
 *
 * Ohne Rebuild erweiterbar: VITE_RYFE_EMBED_HOSTS (Komma-Liste) ergänzt die
 * Default-Hosts beim Build.
 */

/**
 * Standard-Embed-Hosts von RYFE (z. B. der KI-Chat auf ki.ryfe.de) sowie die
 * eigenen Whiteboard-Instanzen selbst — damit RYFE-Boards/Inhalte dieser Domains
 * (Board-in-Board, eigene Seiten) als Web-Embed in eine Szene eingebettet werden
 * dürfen. Exakter Host ODER Subdomain (siehe isRyfeEmbedHost).
 */
const DEFAULT_EMBED_HOSTS = [
  "ki.ryfe.de",
  "whiteboard.mg-ryfe.org",
  "whiteboard-beta.mg-ryfe.org",
];

function configuredHosts(): string[] {
  const extra = (
    (import.meta.env.VITE_RYFE_EMBED_HOSTS as string | undefined) ?? ""
  )
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return [...DEFAULT_EMBED_HOSTS, ...extra];
}

/**
 * True, wenn `url` auf einem erlaubten RYFE-Host liegt — exakter Host ODER eine
 * Subdomain davon (ki.ryfe.de erlaubt auch a.ki.ryfe.de). Protokoll-agnostisch:
 * Der Host zählt, nicht http/https. Wirft nie.
 */
export function isRyfeEmbedHost(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }
  let host: string;
  try {
    host = new URL(String(url)).hostname.toLowerCase();
  } catch {
    return false;
  }
  return configuredHosts().some((h) => host === h || host.endsWith(`.${h}`));
}

/**
 * `validateEmbeddable`-Prop für <Excalidraw>: `true` für RYFE-Hosts, sonst
 * `undefined` (→ Excalidraw prüft mit seiner Standard-Allowlist weiter). So wird
 * die Allowlist nur ERWEITERT, nie ersetzt.
 */
export function validateRyfeEmbeddable(url: string): true | undefined {
  return isRyfeEmbedHost(url) ? true : undefined;
}
